// ====================================================
// src/components/block/liveStreamManagement/videoEffects/SurroundingsEffect.tsx
//
// Fusion de Arrière-plan + Effet flou (ex-BackgroundEffect.tsx/BlurEffect.tsx) :
// un seul bouton dans la rangée "Effets vidéo" — les deux étaient déjà mutuellement
// exclusifs (même groupe 'surroundings', cf. VideoEffect.ts), ce n'était donc que
// deux façades pour un même choix : "que fait-on de ce qui entoure la personne ?"
//   1. fond réel (rien)
//   2. flou (léger/moyen/fort)
//   3. image de fond
// ====================================================

import React, { useEffect, useRef, useState } from 'react';
import { Image as ImageIcon, Plus, RefreshCw, CloudFog } from 'lucide-react';
import { VideoEffect, VideoEffectControlProps, VideoEffectCompositeArgs, drawPersonCutout, drawBlurredFrame } from './VideoEffect';
import {
  BackgroundImage, loadBackgroundImages, getCachedBackgroundImages,
  subscribeBackgroundImages, addBackgroundImage,
} from './backgroundImageStore';
import { ThumbTile } from './ThumbTile';

export type SurroundingsMode = 'real' | 'blur' | 'image';
export type BlurLevel = 'light' | 'medium' | 'strong';

export interface SurroundingsConfig {
  mode: SurroundingsMode;
  /** Pertinent seulement si mode === 'blur'. */
  blurLevel: BlurLevel;
  /** Pertinent seulement si mode === 'image' — id d'une BackgroundImage. */
  backgroundId: string;
  /** Résolue au moment du choix dans renderControls — composite() tourne une fois par
   * frame, elle ne peut pas aller consulter le store de façon asynchrone à chaque appel. */
  url?: string;
}

const BLUR_LEVELS: { id: BlurLevel; label: string }[] = [
  { id: 'light',  label: 'Léger' },
  { id: 'medium', label: 'Moyen' },
  { id: 'strong', label: 'Fort' },
];
const BLUR_PX: Record<BlurLevel, number> = { light: 6, medium: 14, strong: 26 };
// Flou appliqué à l'icône de chaque tuile, à part de BLUR_PX (la valeur réelle du
// compositeur) : sur un aperçu de ~70px, 26px de flou ne laisserait plus rien voir.
const PREVIEW_BLUR_PX: Record<BlurLevel, number> = { light: 1.5, medium: 3, strong: 5 };

// ── Cache d'images décodées ─────────────────────────────────────────────────────
// Partagé entre tous les appelants de composite() (préview locale ET pipeline réel,
// cf. compositeFrame.ts) : le décodage d'une image ne doit se faire qu'une fois.
const decodedImages = new Map<string, HTMLImageElement>();

function getOrLoadImage(url: string): HTMLImageElement | null {
  const existing = decodedImages.get(url);
  if (existing) return existing.complete && existing.naturalWidth > 0 ? existing : null;
  const img = new Image();
  img.src = url;
  decodedImages.set(url, img);
  return null; // pas encore décodée cette frame-ci — le passthrough prend le relais
}

/** Équivalent canvas de `object-fit: cover` — recadre plutôt que déformer. */
function drawImageCover(ctx: CanvasRenderingContext2D, img: HTMLImageElement, w: number, h: number): void {
  const imgRatio = img.naturalWidth / img.naturalHeight;
  const boxRatio = w / h;
  let sx = 0, sy = 0, sw = img.naturalWidth, sh = img.naturalHeight;
  if (imgRatio > boxRatio) {
    sw = img.naturalHeight * boxRatio;
    sx = (img.naturalWidth - sw) / 2;
  } else {
    sh = img.naturalWidth / boxRatio;
    sy = (img.naturalHeight - sh) / 2;
  }
  ctx.drawImage(img, sx, sy, sw, sh, 0, 0, w, h);
}

class SurroundingsEffectImpl extends VideoEffect<SurroundingsConfig> {
  readonly id = 'surroundings';
  readonly label = 'Arrière-plan';
  readonly icon = ImageIcon;
  readonly defaultConfig: SurroundingsConfig = { mode: 'real', blurLevel: 'light', backgroundId: 'none' };
  readonly group = 'surroundings' as const;
  readonly needsSegmentation = true;

  composite({ ctx, width, height, frame, personAlpha, hidePerson, scratch, config }: VideoEffectCompositeArgs<SurroundingsConfig>) {
    if (config.mode === 'blur') {
      drawBlurredFrame(ctx, frame, width, height, BLUR_PX[config.blurLevel]);
    } else if (config.mode === 'image' && config.url) {
      const img = getOrLoadImage(config.url);
      if (img) drawImageCover(ctx, img, width, height);
      else ctx.drawImage(frame, 0, 0, width, height); // pas encore décodée — passthrough plutôt qu'un flash noir
    } else {
      ctx.drawImage(frame, 0, 0, width, height); // 'real'
    }
    // hidePerson : un effet "face" actif (ex. Avatar) veut la personne absente de la
    // frame — ne PAS la coller par-dessus ce fond, pour qu'il reste entier visible à
    // sa place (cf. VideoEffect.wantsPersonHidden, compositeFrame.ts).
    if (personAlpha && !hidePerson) drawPersonCutout(ctx, frame, personAlpha, width, height, scratch);
  }

  renderControls({ config, onChange }: VideoEffectControlProps<SurroundingsConfig>) {
    return <SurroundingsPicker config={config} onChange={onChange} />;
  }
}

export const surroundingsEffect = new SurroundingsEffectImpl();

// ── UI de sélection ──────────────────────────────────────────────────────────

function useBackgroundImages(): BackgroundImage[] {
  const [images, setImages] = useState<BackgroundImage[]>(getCachedBackgroundImages());
  useEffect(() => {
    let mounted = true;
    loadBackgroundImages().then(imgs => { if (mounted) setImages(imgs); });
    return subscribeBackgroundImages(() => setImages(getCachedBackgroundImages()));
  }, []);
  return images;
}

const SurroundingsPicker: React.FC<VideoEffectControlProps<SurroundingsConfig>> = ({ config, onChange }) => {
  const images = useBackgroundImages();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFile = async (file: File | null) => {
    if (!file) return;
    setError(null);
    setUploading(true);
    try {
      const image = await addBackgroundImage(file);
      onChange({ ...config, mode: 'image', backgroundId: image.id, url: image.url });
    } catch (err) {
      console.warn('[SurroundingsEffect] échec de l\'ajout d\'une image :', err);
      setError('Échec de l\'ajout');
    } finally {
      setUploading(false);
    }
  };

  // Plus de tuile "Fond réel" : ce choix est déjà porté par le bouton d'effet lui-même
  // dans la rangée "Effets vidéo" (le désactiver là revient au fond réel) — la tuile
  // ici faisait doublon.
  const blurTiles: React.ReactNode[] = BLUR_LEVELS.map(lvl => (
    <ThumbTile key={`blur-${lvl.id}`} active={config.mode === 'blur' && config.blurLevel === lvl.id} label={lvl.label}
      onClick={() => onChange({ ...config, mode: 'blur', blurLevel: lvl.id })}>
      <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {/* L'icône est réellement floutée au niveau qu'elle représente — la tuile
            EST la démonstration, pas juste un pictogramme à côté d'un texte. */}
        <CloudFog style={{ width: 26, height: 26, color: '#94a3b8', filter: `blur(${PREVIEW_BLUR_PX[lvl.id]}px)` }} />
      </div>
    </ThumbTile>
  ));

  const backgroundTiles: React.ReactNode[] = [
    ...images.map(image => (
      <ThumbTile key={image.id} active={config.mode === 'image' && config.backgroundId === image.id} label={image.label}
        onClick={() => onChange({ ...config, mode: 'image', backgroundId: image.id, url: image.url })}>
        <img src={image.url} alt={image.label} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
      </ThumbTile>
    )),
    <button
      key="add"
      type="button"
      onClick={() => fileInputRef.current?.click()}
      disabled={uploading}
      title="Ajouter une image de fond"
      style={{
        aspectRatio: '1', width: '100%', borderRadius: 8, border: '1.5px dashed #475569',
        background: 'rgba(255,255,255,0.03)', color: '#64748b',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: uploading ? 'wait' : 'pointer',
      }}
    >
      {uploading
        ? <RefreshCw style={{ width: 16, height: 16, animation: 'hls-blink 1s linear infinite' }} />
        : <Plus style={{ width: 18, height: 18 }} />}
    </button>,
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: '#cbd5e1' }}>Flou</span>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>{blurTiles}</div>
      </div>

      <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }} />

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: '#cbd5e1' }}>Arrière-plan</span>
        {/* Grille à 4 colonnes pour l'instant (pas le carrousel à flèches des autres
            rangées) : des tuiles plus grandes et surtout de taille identique — une
            grille CSS étire ses cellules par défaut, contrairement aux items du
            carrousel qui laissaient chaque tuile se dimensionner à son contenu. */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>{backgroundTiles}</div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={e => { const file = e.target.files?.[0] ?? null; e.target.value = ''; handleFile(file); }}
      />

      {error && <span style={{ fontSize: 10, color: '#fca5a5' }}>{error}</span>}
    </div>
  );
};
